import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Close, Person, Email, Phone, LocationOn, Business,
  Star, CalendarMonth, Badge,
} from '@mui/icons-material';
import { User, Company } from 'modules/Interfaces/Company';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';

interface MembershipEntry {
  companyId: string;
  companyName: string;
  isPrimary: boolean;
  moduleAndRole: Array<{ module: string; role: string }>;
  joinedAt: string | null;
  status: string;
}

interface UserViewDialogProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  companies: Company[];
}

const formatDate = (date: Date | string | undefined): string => {
  if (!date) return 'N/A';
  try {
    const d = date instanceof Date ? date : new Date(date);
    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch { return 'N/A'; }
};

export const UserViewDialog: React.FC<UserViewDialogProps> = ({ open, onClose, user, companies }) => {
  const [memberships, setMemberships] = useState<MembershipEntry[]>([]);
  const [membershipsLoading, setMembershipsLoading] = useState(false);

  const getCompanyName = useCallback((companyId: string): string => {
    if (!companyId) return 'N/A';
    return companies.find(c => c._id === companyId)?.name || 'Unknown Company';
  }, [companies]);

  useEffect(() => {
    if (open && user?._id) {
      setMembershipsLoading(true);
      apiClient.get(`/user/${user._id}/memberships`)
        .then((result: any) => {
          const data = result.data || result;
          setMemberships(Array.isArray(data) ? data : []);
        })
        .catch((err: any) => Logger.error('Failed to fetch memberships:', err))
        .finally(() => setMembershipsLoading(false));
    } else {
      setMemberships([]);
    }
  }, [open, user?._id]);

  if (!user) return null;

  const profile = user.profile;
  const fullName = `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim() || 'N/A';
  const statusLower = user.status?.toLowerCase() || '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}>
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid', borderColor: 'divider', pb: 2,
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Person color="primary" />
          <Typography variant="h5" component="span">User Details</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '24px !important' }}>
        <Grid container spacing={3}>

          {/* Header: Name + Status */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h6">{fullName}</Typography>
              <Chip
                label={user.status?.toUpperCase() || 'UNKNOWN'}
                size="small"
                color={statusLower === 'active' ? 'success' : statusLower === 'pending' ? 'warning' : statusLower === 'inactive' ? 'error' : 'default'}
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              ID: {user._id}
            </Typography>
          </Grid>

          {/* Basic Information */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Badge color="primary" /> Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Email</Typography>
                    <Typography variant="body1">{user.email || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Mobile</Typography>
                    <Typography variant="body1">{user.mobile || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Date of Birth</Typography>
                    <Typography variant="body1">{formatDate(profile?.dob)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Address Information */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn color="primary" /> Address
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {profile?.addressLine1 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Address Line 1</Typography>
                      <Typography variant="body1">{profile.addressLine1}</Typography>
                    </Box>
                  )}
                  {profile?.addressLine2 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Address Line 2</Typography>
                      <Typography variant="body1">{profile.addressLine2}</Typography>
                    </Box>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">City</Typography>
                      <Typography variant="body1">{profile?.city || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">State</Typography>
                      <Typography variant="body1">{profile?.state || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">Country</Typography>
                      <Typography variant="body1">{profile?.country || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">ZIP Code</Typography>
                      <Typography variant="body1">{profile?.zip || 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Company Memberships */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business color="primary" /> Company Memberships
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {membershipsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : memberships.length === 0 ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      No memberships data available.
                    </Typography>
                    {/* Fallback to inline data */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="subtitle2">{getCompanyName(user.companyId)}</Typography>
                      <Chip label="Primary" size="small" color="warning" variant="outlined" sx={{ fontWeight: 700, height: 20 }} />
                    </Box>
                    {(user.moduleAndRole || []).length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 3 }}>
                        {user.moduleAndRole!.map((mr, idx) => (
                          <Chip key={idx} label={`${mr.module}: ${mr.role}`} size="small"
                            color={mr.role === 'admin' ? 'warning' : 'default'}
                            sx={(t) => ({
                              fontWeight: 'bold',
                              ...(mr.role !== 'admin' && {
                                backgroundColor: t.palette.mode === 'dark' ? t.palette.grey[700] : t.palette.grey[300],
                                color: t.palette.mode === 'dark' ? t.palette.common.white : t.palette.common.black,
                              }),
                            })} />
                        ))}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {memberships.map((m) => (
                      <Card key={m.companyId} variant="outlined" sx={{
                        borderLeft: m.isPrimary ? '4px solid' : '1px solid',
                        borderLeftColor: m.isPrimary ? 'warning.main' : 'divider',
                      }}>
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: m.isPrimary ? 700 : 400, flex: 1 }}>
                              {m.companyName}
                            </Typography>
                            {m.isPrimary && (
                              <Chip label="Primary" size="small" color="warning" variant="outlined"
                                icon={<Star sx={{ fontSize: '14px !important' }} />}
                                sx={{ fontWeight: 700, height: 22 }} />
                            )}
                            <Chip label={m.status?.toUpperCase() || 'ACTIVE'} size="small"
                              color={m.status?.toLowerCase() === 'active' ? 'success' : 'default'}
                              sx={{ height: 22, fontSize: '0.7rem' }} />
                          </Box>
                          {m.joinedAt && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <CalendarMonth sx={{ fontSize: 12 }} /> Joined: {formatDate(m.joinedAt)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block', mb: 0.75 }}>
                            {m.companyId}
                          </Typography>
                          {m.moduleAndRole.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {m.moduleAndRole.map((mr, idx) => (
                                <Chip key={idx} label={`${mr.module}: ${mr.role}`} size="small"
                                  color={mr.role === 'admin' ? 'warning' : 'default'}
                                  sx={(t) => ({
                                    fontWeight: 'bold',
                                    ...(mr.role !== 'admin' && {
                                      backgroundColor: t.palette.mode === 'dark' ? t.palette.grey[700] : t.palette.grey[300],
                                      color: t.palette.mode === 'dark' ? t.palette.common.white : t.palette.common.black,
                                    }),
                                  })} />
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* System Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>System Information</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" display="block">Created At</Typography>
                    <Typography variant="body1">{formatDate(user.createdAt)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" display="block">Last Updated</Typography>
                    <Typography variant="body1">{formatDate(user.updatedAt)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" display="block">Primary Company</Typography>
                    <Typography variant="body1">{getCompanyName(user.companyId)}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} variant="outlined" color="secondary">Close</Button>
      </DialogActions>
    </Dialog>
  );
};
